import { useMemo, useRef } from "react"

export type StableBlocksResult = {
  frozenBlocks: string[]
  activeTail: string
}

/**
 * Finds the index of the next non-empty line starting from `startIdx`.
 */
function findNextNonEmptyLine(lines: string[], startIdx: number): number {
  for (let i = startIdx; i < lines.length; i++) {
    if (lines[i].trim() !== "") return i
  }
  return -1
}

/**
 * Finds safe split points in parsed HTML/markdown content.
 * A safe split point is a position in the string where a blank line occurs that is:
 * 1. NOT inside a fenced code block (``` or ~~~)
 * 2. NOT inside a math block ($$)
 * 3. NOT followed by a list continuation (-, *, +, digit.)
 * 4. NOT followed by a table row (|)
 * 5. Has at least one more blank line after it (stability guarantee)
 */
function findSafeSplitPoints(content: string): number[] {
  const points: number[] = []
  let inCodeFence = false
  let inMathBlock = false
  let codeFenceMarker: string | null = null

  const lines = content.split("\n")
  let charOffset = 0

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trimStart()

    // Track code fence state
    if (!inMathBlock) {
      const fenceMatch = trimmed.match(/^(```|~~~)/)
      if (fenceMatch) {
        if (!inCodeFence) {
          inCodeFence = true
          codeFenceMarker = fenceMatch[1]
        } else if (trimmed.startsWith(codeFenceMarker!)) {
          inCodeFence = false
          codeFenceMarker = null
        }
      }
    }

    // Track math block state (count $$ on this line)
    if (!inCodeFence) {
      let searchFrom = 0
      for (;;) {
        const idx = line.indexOf("$$", searchFrom)
        if (idx === -1) break
        inMathBlock = !inMathBlock
        searchFrom = idx + 2
      }
    }

    // Check for blank line (potential split point)
    if (line === "" && i > 0 && !inCodeFence && !inMathBlock) {
      const splitPos = charOffset

      // Check what follows this blank line
      const nextNonEmptyLineIdx = findNextNonEmptyLine(lines, i + 1)
      if (nextNonEmptyLineIdx !== -1) {
        const nextLine = lines[nextNonEmptyLineIdx].trimStart()

        // Skip if followed by list continuation
        if (/^[-*+]\s/.test(nextLine) || /^\d+[.)]\s/.test(nextLine)) {
          charOffset += line.length + 1
          continue
        }
        // Skip if followed by table row
        if (nextLine.startsWith("|")) {
          charOffset += line.length + 1
          continue
        }

        // Stability check: must have another blank line after this point
        const remainder = content.substring(splitPos + 1)
        if (remainder.includes("\n\n")) {
          points.push(splitPos)
        }
      }
    }

    charOffset += line.length + 1 // +1 for the \n
  }

  return points
}

/**
 * During streaming, splits parsed HTML into frozen (memoized) blocks and an
 * active tail that re-renders on each chunk. When not streaming, returns
 * everything as a single active tail with zero overhead.
 */
export function useStableBlocks(parsedHtml: string, isStreaming: boolean): StableBlocksResult {
  const frozenBlocksRef = useRef<string[]>([])
  const frozenLengthRef = useRef(0)

  return useMemo(() => {
    // When not streaming, return everything as active tail (zero overhead)
    if (!isStreaming) {
      frozenBlocksRef.current = []
      frozenLengthRef.current = 0
      return { frozenBlocks: [], activeTail: parsedHtml }
    }

    const splitPoints = findSafeSplitPoints(parsedHtml)

    // Need at least 2 split points to freeze anything
    if (splitPoints.length < 2) {
      return {
        frozenBlocks: frozenBlocksRef.current,
        activeTail: parsedHtml.substring(frozenLengthRef.current),
      }
    }

    // Freeze up to the second-to-last split point (conservative)
    const freezeUpTo = splitPoints[splitPoints.length - 2]

    // Only extend frozen blocks, never shrink
    if (freezeUpTo > frozenLengthRef.current) {
      const newFrozenContent = parsedHtml.substring(frozenLengthRef.current, freezeUpTo)
      const newBlocks = newFrozenContent.split(/\n\n+/).filter((b) => b.trim() !== "")

      frozenBlocksRef.current = [...frozenBlocksRef.current, ...newBlocks]
      frozenLengthRef.current = freezeUpTo
    }

    return {
      frozenBlocks: frozenBlocksRef.current,
      activeTail: parsedHtml.substring(frozenLengthRef.current),
    }
  }, [parsedHtml, isStreaming])
}
